import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  getAllAuditLogs,
  getEntityAuditLogs,
  AuditLog,
  AuditLogFilters,
  EntityType,
  AuditAction,
} from '../services/auditLogs';
import './AdminAuditLogsPage.css';

const AdminAuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * limit;
      const result = await getAllAuditLogs(filters, limit, offset);
      setLogs(result.logs);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getActionLabel = (action: AuditAction): string => {
    const labels: Record<AuditAction, string> = {
      CREATE: 'Erstellt',
      UPDATE: 'Aktualisiert',
      DELETE: 'Gelöscht',
      STATUS_CHANGE: 'Status geändert',
    };
    return labels[action] || action;
  };

  const getEntityTypeLabel = (entityType: EntityType): string => {
    const labels: Record<EntityType, string> = {
      Invoice: 'Rechnung',
      User: 'Benutzer',
      SellerConfig: 'Verkäufer-Konfiguration',
      Order: 'Bestellung',
    };
    return labels[entityType] || entityType;
  };

  const toggleExpand = (logId: string) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <AdminHeader />
      <div className="container">
        <div className="card">
          <h2>Audit-Logs</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Hier sehen Sie alle Änderungen an kritischen Entitäten (Rechnungen, Benutzer,
            Konfigurationen, Bestellungen).
          </p>

          {/* Filter */}
          <div className="audit-logs-filters">
            <div className="filter-row">
              <div className="form-group">
                <label htmlFor="entityType">Entitätstyp:</label>
                <select
                  id="entityType"
                  value={filters.entityType || ''}
                  onChange={e => handleFilterChange('entityType', e.target.value)}
                >
                  <option value="">Alle</option>
                  <option value="Invoice">Rechnung</option>
                  <option value="User">Benutzer</option>
                  <option value="SellerConfig">Verkäufer-Konfiguration</option>
                  <option value="Order">Bestellung</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="action">Aktion:</label>
                <select
                  id="action"
                  value={filters.action || ''}
                  onChange={e => handleFilterChange('action', e.target.value)}
                >
                  <option value="">Alle</option>
                  <option value="CREATE">Erstellt</option>
                  <option value="UPDATE">Aktualisiert</option>
                  <option value="DELETE">Gelöscht</option>
                  <option value="STATUS_CHANGE">Status geändert</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="entityId">Entitäts-ID:</label>
                <input
                  id="entityId"
                  type="text"
                  placeholder="z.B. clx..."
                  value={filters.entityId || ''}
                  onChange={e => handleFilterChange('entityId', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="changedBy">Geändert von:</label>
                <input
                  id="changedBy"
                  type="text"
                  placeholder="User-ID oder Username"
                  value={filters.changedBy || ''}
                  onChange={e => handleFilterChange('changedBy', e.target.value)}
                />
              </div>
            </div>

            <div className="filter-row">
              <div className="form-group">
                <label htmlFor="startDate">Von Datum:</label>
                <input
                  id="startDate"
                  type="datetime-local"
                  value={filters.startDate || ''}
                  onChange={e => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">Bis Datum:</label>
                <input
                  id="endDate"
                  type="datetime-local"
                  value={filters.endDate || ''}
                  onChange={e => handleFilterChange('endDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <button onClick={loadLogs} className="btn btn-primary">
                  Filtern
                </button>
                <button
                  onClick={handleResetFilters}
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px' }}
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
          </div>

          {/* Results Info */}
          <div style={{ marginTop: '20px', marginBottom: '10px', color: '#666' }}>
            {total > 0 ? (
              <p>
                Zeige {logs.length} von {total} Einträgen (Seite {currentPage} von {totalPages})
              </p>
            ) : (
              <p>Keine Einträge gefunden</p>
            )}
          </div>

          {/* Logs Table */}
          {loading ? (
            <div className="loading">Lade Audit-Logs...</div>
          ) : logs.length === 0 ? (
            <div className="empty-state">Keine Audit-Logs gefunden</div>
          ) : (
            <>
              <div className="audit-logs-table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Zeitstempel</th>
                      <th>Entität</th>
                      <th>Entitäts-ID</th>
                      <th>Aktion</th>
                      <th>Geändert von</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <React.Fragment key={log.id}>
                        <tr>
                          <td>{formatDate(log.createdAt)}</td>
                          <td>{getEntityTypeLabel(log.entityType)}</td>
                          <td>
                            <code style={{ fontSize: '0.85em' }}>{log.entityId}</code>
                          </td>
                          <td>
                            <span className={`action-badge action-${log.action.toLowerCase()}`}>
                              {getActionLabel(log.action)}
                            </span>
                          </td>
                          <td>
                            {log.changedByUsername || log.changedBy || 'SYSTEM'}
                            {log.ipAddress && (
                              <div style={{ fontSize: '0.8em', color: '#999', marginTop: '2px' }}>
                                IP: {log.ipAddress}
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={() => toggleExpand(log.id)}
                              className="btn btn-sm btn-secondary"
                            >
                              {expandedLogId === log.id ? 'Weniger' : 'Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedLogId === log.id && (
                          <tr>
                            <td colSpan={6} className="log-details">
                              <div className="log-details-content">
                                {log.description && (
                                  <div>
                                    <strong>Beschreibung:</strong> {log.description}
                                  </div>
                                )}
                                {log.changedFields && log.changedFields.length > 0 && (
                                  <div>
                                    <strong>Geänderte Felder:</strong>{' '}
                                    {log.changedFields.join(', ')}
                                  </div>
                                )}
                                {log.userAgent && (
                                  <div>
                                    <strong>User-Agent:</strong>{' '}
                                    <span style={{ fontSize: '0.9em' }}>{log.userAgent}</span>
                                  </div>
                                )}
                                {log.oldData && (
                                  <div>
                                    <strong>Alte Daten:</strong>
                                    <pre className="json-preview">
                                      {JSON.stringify(log.oldData, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.newData && (
                                  <div>
                                    <strong>Neue Daten:</strong>
                                    <pre className="json-preview">
                                      {JSON.stringify(log.newData, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                  >
                    Zurück
                  </button>
                  <span style={{ margin: '0 15px' }}>
                    Seite {currentPage} von {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="btn btn-secondary"
                  >
                    Weiter
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLogsPage;

